import {
  Suspense,
  useEffect,
  useState,
  LazyExoticComponent,
  PropsWithChildren,
  useRef,
} from 'react';

import debounce from 'lodash/debounce';

import LayoutLoader from '../components/LayoutLoader';

interface WindowSizeState {
  width?: number;
  height?: number;
}

export const desktopBreakpoint = 600;

export const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState<WindowSizeState>({
    width: undefined,
    height: undefined,
  });

  useEffect(() => {
    function handleResize() {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }

    window.addEventListener('resize', debounce(handleResize, 100));

    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowSize;
};

type MediaType = 'mobile' | 'desktop';

export const useMedia = ({ type }: { type: MediaType }) => {
  const { width } = useWindowSize();

  if (!width) return false;

  switch (type) {
    case 'mobile':
      return width < desktopBreakpoint;
    case 'desktop':
      return width >= desktopBreakpoint;
    default:
      throw new Error(
        `Invalid usage of useMedia, supported media types are: [mobile, desktop], but received: ${type}`,
      );
  }
};

interface Props<P> {
  getLazyDesktopComponent: LazyExoticComponent<any>;
  getLazyMobileComponent: LazyExoticComponent<any>;
  innerProps?: P;
}

export function ResponsiveFirstWrapper<P extends object>({
  getLazyDesktopComponent,
  getLazyMobileComponent,
  children,
  innerProps,
}: PropsWithChildren<Props<P>>) {
  const isMobile = useMedia({ type: 'mobile' });
  const [Component, setComponent] = useState<any>(null);
  const isMounted = useRef<boolean>(true);

  useEffect(() => {
    if (process.env.NODE_ENV === 'production') {
      // TODO remove this after mobile version is supported
      return setComponent(getLazyDesktopComponent);
    }

    if (isMobile) {
      if (isMounted.current) setComponent(getLazyMobileComponent);
    } else {
      if (isMounted.current) setComponent(getLazyDesktopComponent);
    }

    setComponent(getLazyDesktopComponent);

    return () => {
      isMounted.current = false;
    };
  }, [isMobile, getLazyDesktopComponent, getLazyMobileComponent]);
  return (
    <Suspense fallback={<LayoutLoader />}>
      {Component && <Component {...innerProps}>{children}</Component>}
    </Suspense>
  );
}
